import { useContext, useEffect, useState } from "react";
import { useAccountAmount } from "@shared/features/onboarding/hooks";
import {
  CONFIRM_AUTO_TRADING_MESSAGE,
  TRADING_METHOD_TO_SKIP_CREATE_BOT,
} from "@shared/features/onboarding/constants";
import {
  OnboardingDetailedStep,
  TradingMethodType,
} from "@shared/features/onboarding/types";
import { sharedLib } from "@shared/services/shared.service";
import { getDisplayResultMessageFunction } from "@shared/utils/misc";
import { skipToken } from "@reduxjs/toolkit/query";
import { GroupOverviewContext } from "@shared/features/onboarding/GroupOverview.context";
import { useClientsData } from "@shared/utils/hooks/misc";
import { BotData, LanguageNameSpaces } from "@shared/types";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";

interface useGroupOverviewProps {
  queryGroupId: string | number | null;
  displayResultMessage: ReturnType<typeof getDisplayResultMessageFunction>;
  changeStep?: (
    step: OnboardingDetailedStep,
    queryToAppend?: Record<string, string>
  ) => void;
  onFinishOnboarding?: (botData?: BotData) => void;
}

const useGroupOverview = ({
  queryGroupId,
  displayResultMessage,
  changeStep,
  onFinishOnboarding,
}: useGroupOverviewProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.ONBOARDING);
  const { setSignalsGroupId, selectedAccountId, signalsGroupId } =
    useContext(GroupOverviewContext)!;

  const groupId = Number(queryGroupId);
  const [skipOnboardingModalVisible, setSkipOnboardingModalVisible] =
    useState(false);

  const { getAccountExchangeById } = useClientsData();
  const { accountTotalAndAvailableAmountQuery } = useAccountAmount();

  const { currentData: groupData, ...getGroupDataResult } =
    sharedLib.sharedApi.common.useGetGroupDataQuery(
      groupId ? { sub_group_id: groupId } : skipToken
    );

  const accountExchange = selectedAccountId
    ? getAccountExchangeById(selectedAccountId)
    : undefined;

  const [
    subscribeToGroupFreeTrial,
    { isLoading: subscribeToGroupFreeTrialLoading },
  ] = sharedLib.sharedApi.configuration.useSubscribeToGroupFreeTrialMutation();

  const [
    subscribeToGroupFreeSubscription,
    { isLoading: subscribeToGroupFreeSubscriptionLoading },
  ] =
    sharedLib.sharedApi.configuration.useSubscribeToGroupFreeSubscriptionMutation();

  useEffect(() => {
    if (queryGroupId === null)
      changeStep?.(OnboardingDetailedStep.FOLLOW_SIGNALS_GROUP);
  }, [queryGroupId]);

  useEffect(() => {
    if (groupId) {
      setSignalsGroupId(groupId);
    }
  }, [groupId]);

  const subscribeGroup = () => {
    let subscribeGroupEndpoint;
    if (!groupData?.expiration_timestamp) {
      if (groupData?.has_free_trial)
        subscribeGroupEndpoint = subscribeToGroupFreeTrial;
      else if (groupData?.is_free)
        subscribeGroupEndpoint = subscribeToGroupFreeSubscription;
    }

    if (subscribeGroupEndpoint && signalsGroupId)
      return displayResultMessage(
        subscribeGroupEndpoint({
          sub_group_id: signalsGroupId,
        }).unwrap()
      );
  };

  const startAutoTradingLoading =
    subscribeToGroupFreeTrialLoading || subscribeToGroupFreeSubscriptionLoading;

  const isLoading =
    !groupId ||
    getGroupDataResult.isFetching ||
    accountTotalAndAvailableAmountQuery.isLoading;

  const baseAnalyticsProps = {
    marketplaceGroupId: groupData?.id,
    groupName: groupData?.name,
    clientExchange: accountExchange || "",
    copiedBotLeverage: undefined,
    copiedBotSymbol: undefined,
  };

  const getConfirmMessage = () => {
    return `${t(
      CONFIRM_AUTO_TRADING_MESSAGE
    )} By clicking 'Create Bot' you confirm you understand.`;
  };

  const overviewProps = {
    onFinishOnboarding,
    skipText: t(
      TRADING_METHOD_TO_SKIP_CREATE_BOT[TradingMethodType.SIGNALS_GROUP]
    ),
    onSkipClick: () => setSkipOnboardingModalVisible(true),
  };

  return {
    isLoading,
    groupData,
    startAutoTradingLoading,
    skipOnboardingModalVisible,
    setSkipOnboardingModalVisible,
    baseAnalyticsProps,
    subscribeGroup,
    getConfirmMessage,
    overviewProps,
  };
};

export default useGroupOverview;
